import { useState } from "react";
import CopyCode from "../../CopyCode/CopyCode";

const RadioButton = () => {
  const [selected, setSelected] = useState("option1");
  const [size, setSize] = useState("medium");

  return (
    <div className="flex flex-col gap-y-10 p-6">
      {/* BASIC RADIOS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Basic Radio Buttons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Simple Options</label>
            <div className="space-y-2">
              {["Option 1", "Option 2", "Option 3"].map((opt, i) => (
                <label key={opt} className="flex items-center cursor-pointer">
                  <input type="radio" name="group1" value={`opt${i}`} checked={selected === `opt${i}`} onChange={(e) => setSelected(e.target.value)} className="w-4 h-4" />
                  <span className="ml-2 text-sm text-slate-300">{opt}</span>
                </label>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<label className="flex items-center">
  <input type="radio" name="group" value="1" className="w-4 h-4" />
  <span className="ml-2 text-slate-300">Option 1</span>
</label>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Vertical Stack</label>
            <fieldset className="space-y-2 border border-slate-600 p-3 rounded">
              {["Python", "JavaScript", "TypeScript"].map(lang => (
                <label key={lang} className="flex items-center cursor-pointer">
                  <input type="radio" name="languages" value={lang} className="w-4 h-4" />
                  <span className="ml-2 text-sm text-slate-300">{lang}</span>
                </label>
              ))}
            </fieldset>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<fieldset className="space-y-2 border border-slate-600 p-3 rounded">
  <label className="flex items-center">
    <input type="radio" name="lang" className="w-4 h-4" />
  </label>
</fieldset>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Horizontal</label>
            <div className="flex gap-4">
              {["Yes", "No", "Maybe"].map(val => (
                <label key={val} className="flex items-center cursor-pointer">
                  <input type="radio" name="group2" value={val} className="w-4 h-4" />
                  <span className="ml-1 text-sm text-slate-300">{val}</span>
                </label>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="flex gap-4">
  <label className="flex items-center">
    <input type="radio" name="group" className="w-4 h-4" />
  </label>
</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Descriptions</label>
            <div className="space-y-2">
              {[{label: "Free", desc: "Limited features"}, {label: "Pro", desc: "Full access"}].map(item => (
                <label key={item.label} className="flex items-start cursor-pointer p-2 border border-slate-600 rounded-lg hover:bg-slate-800">
                  <input type="radio" name="group3" value={item.label} className="w-4 h-4 mt-0.5" />
                  <div className="ml-2">
                    <span className="text-sm text-slate-300 font-medium">{item.label}</span>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<label className="flex items-start cursor-pointer">
  <input type="radio" className="w-4 h-4 mt-0.5" />
  <div className="ml-2">
    <span className="text-sm">Option</span>
  </div>
</label>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Card Style</label>
            <div className="space-y-2">
              {["Bronze", "Silver", "Gold"].map(tier => (
                <label key={tier} className="flex items-center p-3 border-2 border-slate-600 rounded-lg cursor-pointer hover:border-amber-400">
                  <input type="radio" name="tier" value={tier} className="w-4 h-4" />
                  <span className="ml-2 text-sm text-slate-300 font-medium">{tier} Plan</span>
                </label>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<label className="flex items-center p-3 border-2 border-slate-600 rounded-lg">
  <input type="radio" className="w-4 h-4" />
  <span className="ml-2">Option</span>
</label>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Disabled State</label>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input type="radio" name="group4" value="1" className="w-4 h-4" />
                <span className="ml-2 text-sm text-slate-300">Available</span>
              </label>
              <label className="flex items-center cursor-not-allowed opacity-50">
                <input type="radio" name="group4" value="2" disabled className="w-4 h-4" />
                <span className="ml-2 text-sm text-slate-500">Disabled</span>
              </label>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<input type="radio" disabled className="w-4 h-4" />
<span className="text-slate-500">Disabled</span>`} />
          </div>
        </div>
      </div>

      {/* STYLED RADIOS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Styled Variants</h2>
        <div className="grid grid-cols-1 gap-4">
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Colored Radios</label>
            <div className="flex flex-wrap gap-4">
              {[{col: "cyan", name: "Cyan"}, {col: "purple", name: "Purple"}, {col: "pink", name: "Pink"}].map(item => (
                <label key={item.col} className="flex items-center cursor-pointer">
                  <input type="radio" name="colors" value={item.col} className={`w-5 h-5 accent-${item.col}-500`} />
                  <span className="ml-2 text-sm text-slate-300">{item.name}</span>
                </label>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<input type="radio" className="w-5 h-5 accent-amber-500" />`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Grid Layout (2x2)</label>
            <div className="grid grid-cols-2 gap-3">
              {["A", "B", "C", "D"].map(letter => (
                <label key={letter} className="flex items-center p-3 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-800">
                  <input type="radio" name="grid" value={letter} className="w-4 h-4" />
                  <span className="ml-2 text-sm text-slate-300">Option {letter}</span>
                </label>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="grid grid-cols-2 gap-3">
  <label className="flex items-center p-3 border rounded-lg">
    <input type="radio" className="w-4 h-4" />
  </label>
</div>`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadioButton;



